import React from 'react';
import SectionRenderer from '../SectionRenderer';

/**
 * GroupRenderer - Renders multiple components horizontally in a group
 * Supports any component type that can be rendered by SectionRenderer
 * 
 * Usage in layoutConfig:
 * {
 *   type: 'group',
 *   width: 12,
 *   title: 'Optional Group Title',
 *   layout: 'horizontal', // 'horizontal' | 'vertical' | 'grid'
 *   columns: 3, // for grid layout
 *   gap: '1rem', // spacing between items
 *   items: [
 *     { type: 'basic-stat-card', ... },
 *     { type: 'line-chart', ... },
 *     { type: 'donut-chart', ... }
 *   ]
 * }
 */
export default function GroupRenderer({ section }) {
    const { 
        title, 
        subtitle = '', 
        layout = 'horizontal', 
        columns = 2, 
        gap = '1rem',
        items = [],
        theme = {}
    } = section;

    if (!items.length) {
        return (
            <div className="dashboard-new-card">
                <div className="py-6 text-sm text-gray-500">No items in group</div>
            </div>
        );
    }

    const getLayoutStyle = () => {
        switch (layout) {
            case 'vertical':
                return {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: gap
                };
            case 'grid':
                return {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    gap: gap
                };
            case 'horizontal':
            default:
                return {
                    display: 'flex',
                    flexDirection: 'row',
                    gap: gap,
                    alignItems: 'stretch'
                };
        }
    };

    return (
        <div className="dashboard-new-card">
            {(title || subtitle) && (
                <div className="mb-4">
                    {title && <h3 className="text-base font-medium text-[#641C1A]">{title}</h3>}
                    {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
                </div>
            )}
            
            <div style={getLayoutStyle()}>
                {items.map((item, index) => {
                    const key = item.id ? `${item.id}-${index}` : `group-item-${index}`;
                    return (
                        <div key={key} className="group-item">
                            <SectionRenderer section={item} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
